import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Loader2, FileText, CheckCircle } from 'lucide-react';
import { ValidationSession } from '@/types/payroll-validator';

interface LoadingStateProps {
  session: ValidationSession | null;
  pollCount: number;
}

export const LoadingState: React.FC<LoadingStateProps> = ({ session, pollCount }) => {
  const getStatusMessage = () => {
    if (!session) {
      return 'Initializing validation...';
    }

    switch (session.status) {
      case 'uploading':
        return 'Processing uploaded files...';
      case 'parsing':
        return 'Parsing documents and extracting data...';
      case 'validating':
        return 'Running validation checks...';
      default:
        return 'Processing...';
    }
  };

  const getProgress = () => {
    if (!session) return 10;

    switch (session.status) {
      case 'uploading':
        return 20;
      case 'parsing':
        // Prevent division by zero
        if (session.total_files === 0) return 40;
        return 40 + ((session.processed_files / session.total_files) * 30);
      case 'validating':
        return 70 + (pollCount * 2); // Gradually increase
      default:
        return 10;
    }
  };

  const progress = Math.min(getProgress(), 95); // Cap at 95% until complete

  const validationSteps = [
    { label: 'Pension Compliance', checked: progress >= 30 },
    { label: 'Tax Code Validation', checked: progress >= 45 },
    { label: 'National Insurance', checked: progress >= 60 },
    { label: 'HMRC RTI Data', checked: progress >= 75 },
    { label: 'Pay History', checked: progress >= 85 },
    { label: 'Cross-Document Checks', checked: progress >= 90 },
  ];

  return (
    <div className="container mx-auto p-6 max-w-2xl">
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <Card className="w-full">
          <CardContent className="p-8">
            {/* Spinner */}
            <div className="flex justify-center mb-6">
              <div className="relative">
                <Loader2 className="h-16 w-16 text-blue-600 animate-spin" />
                <FileText className="h-8 w-8 text-blue-600 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
              </div>
            </div>

            {/* Status Message */}
            <h2 className="text-2xl font-bold text-center text-gray-900 mb-2">
              {getStatusMessage()}
            </h2>

            {/* Session Info */}
            {session && (
              <p className="text-center text-gray-600 mb-6">
                Processing {session.total_files} file{session.total_files !== 1 ? 's' : ''} 
                {session.total_employees > 0 && (
                  <> • {session.total_employees} employee{session.total_employees !== 1 ? 's' : ''} detected</>
                )}
              </p>
            )}

            {/* Progress Bar */}
            <Progress value={progress} className="mb-8 h-3" />

            {/* Validation Steps */}
            <div className="space-y-3">
              <p className="text-sm font-semibold text-gray-700 mb-3">
                Running validation checks:
              </p>
              {validationSteps.map((step, index) => (
                <div
                  key={index}
                  className={`flex items-center gap-3 p-3 rounded-md transition-colors ${
                    step.checked ? 'bg-green-50' : 'bg-gray-50'
                  }`}
                >
                  {step.checked ? (
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                  ) : (
                    <div className="h-5 w-5 rounded-full border-2 border-gray-300 flex-shrink-0" />
                  )}
                  <span
                    className={`text-sm ${
                      step.checked
                        ? 'text-green-900 font-medium'
                        : 'text-gray-600'
                    }`}
                  >
                    {step.label}
                  </span>
                </div>
              ))}
            </div>

            {/* Fun Fact */}
            <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm text-blue-800 text-center">
                <strong>Did you know?</strong> The average UK employer saves £2,400/year by 
                catching payroll errors before they happen.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

